import {
  Button,
  CircularProgress,
  IconButton,
  SxProps,
  Theme,
} from "@mui/material";

import ButtonActionDelete from "./ButtonActionDelete";
import ButtonActionReject from "./ButtonActionReject";
import {
  useProductNavigation,
  useUpdateProductStatus,
} from "./useActionButton";
import { UserActions } from "../../../../models/Authorisation";
import { PRODUCT_STATUS } from "../../../../models/Product";
import { MODE } from "../../../../stores/formStore";
import { mono } from "../../../../theme/colors";
import PencilIcon from "../../../icons/ui-actions/PencilIcon";

type Props = {
  action: UserActions;
  productId: string;
  productName?: string;
  customStyle?: SxProps<Theme>;
  disabled?: boolean;
};

const ActionButton = ({
  action,
  productId,
  productName,
  customStyle,
  disabled,
}: Props) => {
  const { updateProductStatus, isLoading } = useUpdateProductStatus();
  const { navigateToProduct } = useProductNavigation();

  const handleOnClick = () => {
    switch (action) {
      case UserActions.Approve:
        return updateProductStatus(productId, PRODUCT_STATUS.APPROVED);

      case UserActions.Activate:
        return updateProductStatus(productId, PRODUCT_STATUS.ACTIVE);

      case UserActions.Deactivate:
        return updateProductStatus(productId, PRODUCT_STATUS.DEACTIVATED);

      case UserActions.Review:
      case UserActions.View:
        return navigateToProduct(productId, MODE.VIEW);

      case UserActions.Edit:
        return navigateToProduct(productId, MODE.EDIT);
    }
  };

  if (action === UserActions.Edit) {
    return (
      <IconButton onClick={handleOnClick} data-testid="user-action-edit-button">
        <PencilIcon height={20} fill={mono.grey[2]} />
      </IconButton>
    );
  }

  if (action === UserActions.Delete) {
    return (
      <ButtonActionDelete productId={productId} productName={productName} />
    );
  }

  if (action === UserActions.Reject) {
    return (
      <ButtonActionReject productId={productId} customStyle={customStyle} />
    );
  }

  return (
    <Button
      variant="secondary"
      size="small"
      disabled={isLoading || disabled}
      startIcon={isLoading && <CircularProgress size={20} />}
      onClick={handleOnClick}
      sx={customStyle}
      data-testid="action-button"
    >
      {action}
    </Button>
  );
};

export default ActionButton;
